import { h, Fragment } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import Swatches from 'utils/swatches';
import AngleRightIcon from 'utils/icon/icon-angle-right.svg';
import FullWidthRadio from './full-width-radio';
import Pills from './pills';
import ProductSizeGuide from './product-size-guide';

const VariantSelector = ({
  product,
  currentVariant,
  option,
  setCurrentVariant,
  checkedOptions,
  setCheckedOptions,
  prefix = '',
  template = 'pdp',
  filteredVariants,
}) => {
  const selectedOption = currentVariant?.selectedOptions.find((opt) => opt.name === option.name)?.value;

  const handleVariantChange = (e) => {
    const updatedOption = e.target.dataset.optionName;
    const selectedOptions = currentVariant?.selectedOptions.map((o) =>
      o.name === updatedOption ? { name: updatedOption, value: e.target.value } : o,
    );

    const selectedOptionsValues = Object.fromEntries(selectedOptions.map((o) => [o.name, o.value]));
    const newVariant = filteredVariants.find((v) =>
      v.selectedOptions.every((o) => selectedOptionsValues[o.name] === o.value),
    );

    if (!newVariant) {
      const values = selectedOptions.map((opt) => opt.value);
      const res = filteredVariants.find((variant) => {
        const selectedValues = variant.selectedOptions.map((opt) => opt.value);
        const firstValue = values[0];
        return values.every((value) => selectedValues.includes(value)) || selectedValues.includes(firstValue);
      });
      setCurrentVariant(res);
      setCheckedOptions({ ...checkedOptions, weight: true, size: false });

      if (template === 'pdp') {
        const variantUpdatedEvent = new CustomEvent('currentVariantUpdated', {
          detail: { currentVariant: res },
        });
        document.dispatchEvent(variantUpdatedEvent);
      }
      return;
    }

    if (template === 'pdp') {
      const variantUpdatedEvent = new CustomEvent('currentVariantUpdated', {
        detail: { currentVariant: newVariant },
      });
      document.dispatchEvent(variantUpdatedEvent);
    }

    setCurrentVariant(newVariant);
  };

  const swatches = useMemo(
    () => (
      <Swatches
        product={product}
        currentVariant={currentVariant}
        setCurrentVariant={setCurrentVariant}
        handleVariantChange={handleVariantChange}
        condensed={false}
        template={'pdp'}
        prefix={prefix}
        filteredVariants={filteredVariants}
      />
    ),
    [currentVariant, prefix, filteredVariants],
  );

  const fullWidthRadio = useMemo(
    () => (
      <FullWidthRadio
        product={product}
        currentVariant={currentVariant}
        setCurrentVariant={setCurrentVariant}
        handleVariantChange={handleVariantChange}
        option={option}
        checkedOptions={checkedOptions}
        setCheckedOptions={setCheckedOptions}
        filteredVariants={filteredVariants}
        template={'pdp'}
      />
    ),
    [currentVariant, checkedOptions, filteredVariants],
  );

  const pills = useMemo(
    () => (
      <Pills
        product={product}
        currentVariant={currentVariant}
        handleVariantChange={handleVariantChange}
        option={option}
        checkedOptions={checkedOptions}
        setCheckedOptions={setCheckedOptions}
        prefix={prefix}
        filteredVariants={filteredVariants}
      />
    ),
    [currentVariant, checkedOptions],
  );

  const [openedGiftCard, setOpenedGiftCard] = useState(false);
  const [selectedGiftCardValue, setSelectedGiftCardValue] = useState(
    currentVariant.selectedOptions.find((o) => o.name.toLowerCase() === 'denominations')?.value || '',
  );

  const handleGiftCardClick = (e) => {
    e.preventDefault();
    setOpenedGiftCard((prev) => !prev);
  };

  const handleGiftCardSelect = (value, e) => {
    e?.preventDefault();
    setSelectedGiftCardValue(value);
    setOpenedGiftCard(false);
    handleVariantChange({
      target: {
        value,
        dataset: {
          optionName: option.name,
        },
      },
    });
  };

  const giftCardDropdown = (
    <div className="max-w-[447px]">
      {option.optionValues?.length > 0 && (
        <div className="body-14-med md:body-16-med mb-12 text-faded-black">{window.TMW.Strings.product.giftAmount}</div>
      )}
      {option.optionValues?.[0] && (
        <button
          className={`body-14-med flex w-full items-center justify-between border border-primary bg-grey-6 p-[10px] text-faded-black md:px-15 ${openedGiftCard ? 'rounded-t border-b-0' : 'rounded'} ${option.optionValues?.length > 1 ? 'cursor-pointer' : ''}`}
          onClick={handleGiftCardClick}
        >
          <div className="flex items-center gap-6">{selectedGiftCardValue}</div>
          {option.optionValues?.length > 1 && (
            <span className={`${openedGiftCard ? '-rotate-90' : 'rotate-90'}`}>
              <AngleRightIcon />
            </span>
          )}
        </button>
      )}
      {option.optionValues?.length > 1 && openedGiftCard && (
        <div className="rounded-b border border-primary bg-grey-6">
          {option.optionValues?.map((value) => (
            <div
              className="flex cursor-pointer items-center gap-6 border-b border-grey-5 px-15 py-12 text-faded-black last-of-type:rounded-b"
              key={`${option.name}-${value.name}`}
              onClick={() => handleGiftCardSelect(value.name)}
              onKeyDown={(e) =>
                e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' ? handleGiftCardSelect(value.name, e) : ''
              }
              tabIndex={0}
            >
              {value.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleDonationSelect = (value, e) => {
    e?.preventDefault();
    handleVariantChange({
      target: {
        value,
        dataset: {
          optionName: option.name,
        },
      },
    });
  };

  const donationDropdown = (
    <div className="donation-dropdown min-w-[calc(100%-85px)]">
      {option.optionValues?.length > 0 && (
        <select
          className={`body-14-med relative flex h-full w-full items-center justify-between rounded border-none bg-white p-[15px] text-faded-black md:px-15 ${option.optionValues?.length > 1 ? 'cursor-pointer' : ''}`}
          onChange={(event) => handleDonationSelect(event.target.value)}
        >
          {option.optionValues?.map((value) => (
            <option
              value={value.name}
              key={value.name}
            >
              <span className="price">{value.name}</span>
            </option>
          ))}
        </select>
      )}
    </div>
  );

  return (
    <div
      className={`${option.name.toLowerCase() === 'fabric weight' && option.optionValues.length === 1 ? 'hidden' : 'w-full'}`}
    >
      {option.name.toLowerCase() !== 'donate' && (
        <h2 className="body-14-med md:body-16-med mb-12 flex items-center justify-between text-faded-black">
          {option.name.toLowerCase() === 'color' && (
            <span>
              {option.name}:{' '}
              <span className="body-14-light md:body-16-light inline-block pl-4 text-black">{selectedOption}</span>
            </span>
          )}
          {option.name.toLowerCase() === 'size' && (
            <>
              <span>
                {option.name}:
                {checkedOptions.size && (
                  <span className="body-14-light md:body-16-light inline-block pl-4 text-black">{selectedOption}</span>
                )}
              </span>
              {product?.sizeGuide?.enabled && product?.sizeGuide?.image?.previewImage && (
                <>
                  <button
                    data-size-guide-trigger
                    className="body-14-light md:body-16-light text-black underline-offset-2 hover:underline"
                  >
                    {window.TMW.Strings.product.sizeGuide}
                  </button>
                  <ProductSizeGuide {...product} />
                </>
              )}
            </>
          )}
          {option.name.toLowerCase() === 'fabric weight' && <span>{option.name}:</span>}
        </h2>
      )}
      {option.name.toLowerCase() === 'denominations' && giftCardDropdown}
      {option.name.toLowerCase() === 'donate' && donationDropdown}
      {option.name.toLowerCase() === 'color' && swatches}
      {option.name.toLowerCase() === 'fabric weight' && fullWidthRadio}
      {option.name.toLowerCase() === 'size' && pills}
    </div>
  );
};

export default VariantSelector;
